import _concat from 'lodash/concat';
import _isEqual from 'lodash/isEqual';
import _isNil from 'lodash/isNil';
import _omitBy from 'lodash/omitBy';
import _takeRightWhile from 'lodash/takeRightWhile';
import _takeWhile from 'lodash/takeWhile';
import _uniq from 'lodash/uniq';
import { DateTime } from 'luxon';
import { RRule, Weekday } from 'rrule';
import { TransactionProps } from '../transactions/transactionsTypes';
import {
  CLEAR_STATE,
  type DaysOfWeekShort,
  FrequencyAction,
  type RecurrenceType,
  type RecurrenceWithDates,
  UPDATE_ALIAS,
  UPDATE_DEFAULT,
  UPDATE_DUE_ON,
  UPDATE_EVERY_X_INTERVAL,
  UPDATE_FREQUENCY_3_MONTHS,
  UPDATE_FREQUENCY_90_DAYS,
  UPDATE_FREQUENCY_EVERY_X_DAYS,
  UPDATE_FREQUENCY_EVERY_X_MONTHS,
  UPDATE_FREQUENCY_EVERY_X_WEEKS,
  UPDATE_FREQUENCY_EVERY_X_YEARS,
  UPDATE_FREQUENCY_MONTHLY,
  UPDATE_FREQUENCY_WEEKLY,
  UPDATE_FREQUENCY_YEARLY,
  UPDATE_MONTHLY_BY_DAYS_IN_MONTH,
  UPDATE_MONTHLY_BY_DAY_OF_WEEK,
  UPDATE_MONTHLY_BY_MONTHDAY,
  UPDATE_MONTHLY_BY_WEEKDAY,
  UPDATE_MULTIPLE_BY_DATES,
  UPDATE_ONE_TIME,
  UPDATE_WEEKLY_BY_DAY,
  UPDATE_YEARLY_MONTHS,
  VALIDATE_RECURRENCE,
} from './customFrequencyTypes';
import { AliasEnum, Frequency, FrequencyEnum } from './frequency';

type dateOptions = { name: string; value: number }[];

export const ONE_TIME = 'One-time payment';
export const EVERY_WEEK = 'Every week';
export const EVERY_MONTH = 'Every month';
export const EVERY_YEAR = 'Every year';
export const EVERY_QUARTER = 'Every quarter';
export const EVERY_X_DAYS = 'Every X days';
export const EVERY_X_WEEKS = 'Every X weeks';
export const EVERY_X_MONTHS = 'Every X months';
export const EVERY_X_YEARS = 'Every X years';
export const TWICE_A_MONTH = 'Twice a month';
export const MULTIPLE_DATES = 'Multiple fixed dates';

export const frequencyAliasTypes = {
  oneTimeSimplifi: ONE_TIME,
  everyWeekSimplifi: EVERY_WEEK,
  everyMonthSimplifi: EVERY_MONTH,
  everyYearSimplifi: EVERY_YEAR,
  twiceAMonthSimplifi: TWICE_A_MONTH,
  everyQuarterSimplifi: EVERY_QUARTER,
  everyXDays: EVERY_X_DAYS,
  everyXWeeks: EVERY_X_WEEKS,
  everyXMonths: EVERY_X_MONTHS,
  everyXYears: EVERY_X_YEARS,
  multipleDatesSimplifi: MULTIPLE_DATES,
};

export const frequencyAliases = Object.values(frequencyAliasTypes);
export type FrequencyAliasOptions = (typeof frequencyAliasTypes)[keyof typeof frequencyAliasTypes];

export const CHIP_MONTHS = [
  { label: 'Jan', value: 1 },
  { label: 'Feb', value: 2 },
  { label: 'Mar', value: 3 },
  { label: 'Apr', value: 4 },
  { label: 'May', value: 5 },
  { label: 'Jun', value: 6 },
  { label: 'Jul', value: 7 },
  { label: 'Aug', value: 8 },
  { label: 'Sep', value: 9 },
  { label: 'Oct', value: 10 },
  { label: 'Nov', value: 11 },
  { label: 'Dec', value: 12 },
];

export const SELECT_MONTHS = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

export const weekDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

export const CHIP_DAYS = [
  { label: 'Mon', value: weekDays[0] },
  { label: 'Tue', value: weekDays[1] },
  { label: 'Wed', value: weekDays[2] },
  { label: 'Thu', value: weekDays[3] },
  { label: 'Fri', value: weekDays[4] },
  { label: 'Sat', value: weekDays[5] },
  { label: 'Sun', value: weekDays[6] },
];

export const DAY_SELECT_TYPE = Object.freeze({
  DOM: 'Day of the Month',
  DOW: 'Day of the Week',
});

export const indexToWeekday = (index: number): DaysOfWeekShort[] => [weekDays[index]];
export const weekdayToIndex = (weekday: DaysOfWeekShort[]): number => weekDays.indexOf(weekday[0]);

export const SELECT_DAYS = [
  { label: 'Sunday', value: RRule.SU },
  { label: 'Monday', value: RRule.MO },
  { label: 'Tuesday', value: RRule.TU },
  { label: 'Wednesday', value: RRule.WE },
  { label: 'Thursday', value: RRule.TH },
  { label: 'Friday', value: RRule.FR },
  { label: 'Saturday', value: RRule.SA },
];

export const QUARTERLY_OPTIONS = [{ label: 'Every 90 days' }, { label: 'Every 3 calendar months' }];

export const weekDaysMap: Record<DaysOfWeekShort, Weekday> = Object.freeze({
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
});

export const MIN_DAYS = 1;
export const MAX_DAYS = 90;
export const MIN_WEEKS = 1;
export const MAX_WEEKS = 52;
export const MIN_MONTHS = 1;
export const MAX_MONTHS = 12;
export const MIN_YEARS = 1;
export const MAX_YEARS = 5;

export const { DAILY, WEEKLY, MONTHLY, YEARLY } = RRule;

const {
  oneTimeSimplifi,
  everyWeekSimplifi,
  everyMonthSimplifi,
  everyYearSimplifi,
  everyQuarterSimplifi,
  everyXDays,
  everyXWeeks,
  everyXMonths,
  everyXYears,
  twiceAMonthSimplifi,
  multipleDatesSimplifi,
} = frequencyAliasTypes;

export const FREQUENCIES = [FrequencyEnum.YEARLY, FrequencyEnum.MONTHLY, FrequencyEnum.WEEKLY, FrequencyEnum.DAILY];

/////////////////////////////////////
// Ordinal numbers strings  - 1st, 2nd, etc..
/////////////////////////////////////
const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const generateOptions = (end: number): dateOptions => {
  const days: dateOptions = [];
  for (let i = 1; i <= end; i++) {
    days.push({ name: ordinal(i), value: i });
  }
  if (end === 30) {
    days.push({ name: '3rd Last', value: -3 });
    days.push({ name: '2nd Last', value: -2 });
  }
  days.push({ name: 'Last', value: -1 });
  return days;
};

export const ORDINAL_DAYS = generateOptions(30);
export const ORDINAL_WEEKS = generateOptions(4);

/////////////////////////////////////
// Recurrence validation
/////////////////////////////////////
const hasDaysOfWeek = (model: RecurrenceType) => Boolean(model.byDay && model.byDay.filter((x) => x).length > 0);
const hasMonthDays = (model: RecurrenceType) =>
  Boolean(
    (model.byMonthDay && model.byMonthDay?.length > 0) ||
      (!_isNil(model.byDay) && model.bySetPos && model.bySetPos.filter((x) => x).length > 0),
  );
const hasMonths = (model: RecurrenceType) => Boolean(model.byMonth && model.byMonth.filter((x) => x).length > 0);
const hasSpecificDates = (model: RecurrenceType) => Boolean(model.byDates && model.byDates.filter((x) => x).length > 0);
const isEvery = (model: RecurrenceType) => model.interval === 1;
const isEveryX = (model: RecurrenceType) => Boolean(model.interval && !isNaN(model.interval) && model.interval > 0);
const isDaily = (model: RecurrenceType) => model.frequency === FrequencyEnum.DAILY;
const isWeekly = (model: RecurrenceType) => model.frequency === FrequencyEnum.WEEKLY;
const isMonthly = (model: RecurrenceType) => model.frequency === FrequencyEnum.MONTHLY;
const isYearly = (model: RecurrenceType) => model.frequency === FrequencyEnum.YEARLY;
const isQuarterly = (model: RecurrenceType) => isMonthly(model) && model.interval === 3;
const isOnlyOnce = (model: RecurrenceType) => model.count === 1;

// validate every recurrences
const isValidEveryWeekSimplifi = (model: RecurrenceType) => isWeekly(model) && isEvery(model) && hasDaysOfWeek(model);
const isValidEveryMonthSimplifi = (model: RecurrenceType) => isMonthly(model) && isEvery(model) && hasMonthDays(model);
const isValidEveryYearSimplifi = (model: RecurrenceType) =>
  isYearly(model) && isEvery(model) && hasMonths(model) && hasMonthDays(model);

// validate every X recurrences
const isValidEveryXDays = (model: RecurrenceType) => isDaily(model) && isEveryX(model);
const isValidEveryXWeeks = (model: RecurrenceType) => isWeekly(model) && isEveryX(model) && hasDaysOfWeek(model);
const isValidEveryXMonths = (model: RecurrenceType) => isMonthly(model) && isEveryX(model) && hasMonthDays(model);
const isValidEveryXYears = (model: RecurrenceType) =>
  isYearly(model) && isEveryX(model) && hasMonths(model) && hasMonthDays(model);

// validate special recurrences
const isValidOneTimeSimplifi = (model: RecurrenceType) => isDaily(model) && isEvery(model) && isOnlyOnce(model);
const isValidTwiceAMonth = (model: RecurrenceType) =>
  isMonthly(model) && isEvery(model) && Boolean(model.byMonthDay && model.byMonthDay.length === 2);
const isValidEveryQuarterSimplifi = (model: RecurrenceType) => isQuarterly(model);
const isValidMultipleDatesSimplifi = (model: RecurrenceType) =>
  isYearly(model) && isEvery(model) && hasSpecificDates(model);
const isValidDate = (date: Date) => date instanceof Date && !isNaN(date.getDay());

export const isValidRecurrence = (recurrence: RecurrenceType): boolean => {
  if (!recurrence) return false;

  const recurrenceAlias = mapRecurrenceFrequencyToFrequencyAlias(recurrence);

  switch (recurrenceAlias) {
    case oneTimeSimplifi:
      return isValidOneTimeSimplifi(recurrence);
    case everyWeekSimplifi:
      return isValidEveryWeekSimplifi(recurrence);
    case everyMonthSimplifi:
      return isValidEveryMonthSimplifi(recurrence);
    case everyYearSimplifi:
      return isValidEveryYearSimplifi(recurrence);
    case everyQuarterSimplifi:
      return isValidEveryQuarterSimplifi(recurrence);
    case everyXDays:
      return isValidEveryXDays(recurrence);
    case everyXWeeks:
      return isValidEveryXWeeks(recurrence);
    case everyXMonths:
      return isValidEveryXMonths(recurrence);
    case everyXYears:
      return isValidEveryXYears(recurrence);
    case multipleDatesSimplifi:
      return isValidMultipleDatesSimplifi(recurrence);
    case twiceAMonthSimplifi:
      return isValidTwiceAMonth(recurrence);
    default:
      return false;
  }
};

// for a given client alias, return an AliasEnum value
// i.e. "Every X Weeks" => "EVERY_X_WEEKS"
export const convertClientAliasToQCSAlias = (clientAlias: string): AliasEnum => {
  // on Edit the Alias is already converted, so just return it.
  if (Object.values(AliasEnum)?.includes(clientAlias as AliasEnum)) {
    return clientAlias as AliasEnum;
  }

  switch (clientAlias) {
    case oneTimeSimplifi:
      return AliasEnum.ONE_TIME_PAYMENT;
    case everyWeekSimplifi:
      return AliasEnum.EVERY_WEEK;
    case everyMonthSimplifi:
      return AliasEnum.EVERY_MONTH;
    case everyYearSimplifi:
      return AliasEnum.EVERY_YEAR;
    case everyQuarterSimplifi:
      return AliasEnum.EVERY_QUARTER;
    case everyXDays:
      return AliasEnum.EVERY_X_DAYS;
    case everyXWeeks:
      return AliasEnum.EVERY_X_WEEKS;
    case everyXMonths:
      return AliasEnum.EVERY_X_MONTHS;
    case everyXYears:
      return AliasEnum.EVERY_X_YEARS;
    case multipleDatesSimplifi:
      return AliasEnum.MULTIPLE_FIXED_DATES;
    case twiceAMonthSimplifi:
      return AliasEnum.TWICE_A_MONTH;
    default:
      return AliasEnum.EVERY_MONTH;
  }
};

// for a given AliasEnum value, return an client alias value
// i.e. "EVERY_X_WEEKS" => "Every X Weeks"
const converQCSAliasToClientAlias = (qcsAlias: string): string => {
  switch (qcsAlias) {
    case AliasEnum.ONE_TIME_PAYMENT:
      return oneTimeSimplifi;
    case AliasEnum.EVERY_WEEK:
      return everyWeekSimplifi;
    case AliasEnum.EVERY_MONTH:
      return everyMonthSimplifi;
    case AliasEnum.EVERY_YEAR:
      return everyYearSimplifi;
    case AliasEnum.EVERY_QUARTER:
      return everyQuarterSimplifi;
    case AliasEnum.EVERY_X_DAYS:
      return everyXDays;
    case AliasEnum.EVERY_X_WEEKS:
      return everyXWeeks;
    case AliasEnum.EVERY_X_MONTHS:
      return everyXMonths;
    case AliasEnum.EVERY_X_YEARS:
      return everyXYears;
    case AliasEnum.MULTIPLE_FIXED_DATES:
      return multipleDatesSimplifi;
    case AliasEnum.TWICE_A_MONTH:
      return twiceAMonthSimplifi;
    default:
      return qcsAlias;
  }
};

// for a given alias and interval, return a FrequencyEnum value.   DAILY | WEEKLY | MONTHLY | YEARLY
export const getFrequencyFromAlias = (alias: string): Frequency => {
  // convert alias from user display value to AliasEnum,
  //i.e. "Every 3 weeks" => "EVERY_X_WEEKS", "One-time payment" => "ONE_TIME_PAYMENT"
  const normalized = alias.replace(/[0-9]/g, 'X').replace(/-/g, '_').replace(/ /g, '_').toUpperCase();

  let response;

  switch (normalized) {
    case AliasEnum.ONE_TIME_PAYMENT:
      response = FrequencyEnum.DAILY;
      break;
    case AliasEnum.EVERY_YEAR:
    case AliasEnum.EVERY_X_YEARS:
    case AliasEnum.MULTIPLE_FIXED_DATES:
      response = FrequencyEnum.YEARLY;
      break;
    case AliasEnum.EVERY_WEEK:
    case AliasEnum.EVERY_X_WEEKS:
      response = FrequencyEnum.WEEKLY;
      break;
    case AliasEnum.EVERY_MONTH:
    case AliasEnum.EVERY_X_MONTHS:
    case AliasEnum.TWICE_A_MONTH:
      response = FrequencyEnum.MONTHLY;
      break;
    case AliasEnum.EVERY_X_DAYS:
      response = FrequencyEnum.DAILY;
      break;
    case AliasEnum.EVERY_QUARTER:
      response = FrequencyEnum.MONTHLY;
      break;
    default:
      response = FrequencyEnum.MONTHLY;
      break;
  }

  return response;
};

const convertByDatesToAbsolute = (byDates: string[]): string[] => {
  return byDates.map((date) => {
    const [month, day] = date.split('/').map((item) => Number.parseInt(item));
    if (month >= 1 && month <= 12 && day < 1) {
      const daysInMonth = DateTime.local(2023, month).daysInMonth || 31; // since this is 'yearly' using a non-leap year
      const adjustedDay = daysInMonth + day + 1;
      return month + '/' + adjustedDay;
    }
    return date;
  });
};

// called right before dispatch to QCS
export const recurrenceFromVirtualRecurrence = (virtualRecurrence: RecurrenceType) => {
  const responseObj = <RecurrenceType>_omitBy(virtualRecurrence, _isNil); // remove props with no value assigned
  if (responseObj.alias) {
    responseObj.alias = convertClientAliasToQCSAlias(responseObj.alias);
  }
  if (responseObj.byDates) {
    responseObj.byDates = convertByDatesToAbsolute(responseObj.byDates);
  }

  delete responseObj.isValid;
  delete responseObj.dueOn;
  return responseObj;
};

////////////////////////////////
// State management
////////////////////////////////

export const clearState = {
  byMonthDay: undefined,
  bySetPos: undefined,
  interval: undefined,
  count: undefined,
  isValid: undefined,
  alias: undefined,
  byMonth: undefined,
  byDay: undefined,
  byYearDay: undefined,
  dueOn: undefined,
  byDates: undefined,
  until: undefined,
  byMonthDayOrLastDay: undefined, // removed property
};

const calculateSideEffects = (state: RecurrenceType): RecurrenceType => {
  const newState: RecurrenceType = { ...state };

  newState.isValid = isValidRecurrence(newState);
  return newState;
};

export const recurrenceReducer = (state: RecurrenceType, action: FrequencyAction): RecurrenceType => {
  let newState: RecurrenceType;

  switch (action.type) {
    case UPDATE_ALIAS: {
      // make sure to set the interval to 1 for specific frequencies
      const interval =
        action.payload === EVERY_MONTH || action.payload === EVERY_WEEK || action.payload === EVERY_YEAR
          ? 1
          : state.interval;
      newState = { ...state, alias: action.payload, interval };
      break;
    }
    // actions that clear the state - start
    case CLEAR_STATE: {
      newState = { ...clearState };
      break;
    }
    case UPDATE_ONE_TIME: {
      const dueOn = action.payload ? action.payload : state.dueOn;
      newState = { ...clearState, alias: ONE_TIME, frequency: FrequencyEnum.DAILY, interval: 1, count: 1, dueOn };
      break;
    }
    case UPDATE_FREQUENCY_EVERY_X_DAYS: {
      newState = {
        ...clearState,
        alias: EVERY_X_DAYS,
        frequency: FrequencyEnum.DAILY,
        interval: action.payload.interval,
      };
      break;
    }
    case UPDATE_FREQUENCY_MONTHLY: {
      newState = {
        ...clearState,
        alias: EVERY_MONTH,
        frequency: FrequencyEnum.MONTHLY,
        interval: 1,
        byMonthDay: action.payload,
        byDay: undefined,
        bySetPos: undefined,
      };
      break;
    }
    case UPDATE_FREQUENCY_EVERY_X_MONTHS: {
      newState = {
        ...clearState,
        alias: EVERY_X_MONTHS,
        frequency: FrequencyEnum.MONTHLY,
        interval: action.payload.interval,
      };
      break;
    }
    case UPDATE_FREQUENCY_WEEKLY: {
      newState = {
        ...clearState,
        alias: EVERY_WEEK,
        frequency: FrequencyEnum.WEEKLY,
        interval: 1,
        byDay: action.payload,
      };
      break;
    }
    case UPDATE_FREQUENCY_EVERY_X_WEEKS: {
      newState = { ...clearState, alias: EVERY_X_WEEKS, frequency: FrequencyEnum.WEEKLY, interval: action.payload };
      break;
    }
    case UPDATE_FREQUENCY_EVERY_X_YEARS: {
      newState = { ...clearState, frequency: FrequencyEnum.YEARLY, interval: action.payload.interval };
      break;
    }
    case UPDATE_FREQUENCY_90_DAYS: {
      newState = { ...clearState, alias: EVERY_QUARTER, frequency: FrequencyEnum.DAILY, interval: 90 };
      break;
    }
    case UPDATE_FREQUENCY_3_MONTHS: {
      newState = { ...clearState, alias: EVERY_QUARTER, frequency: FrequencyEnum.MONTHLY, interval: 3 };
      break;
    }
    case UPDATE_FREQUENCY_YEARLY: {
      newState = { ...clearState, frequency: FrequencyEnum.YEARLY, interval: 1 };
      break;
    }
    // actions that modify the current the state
    case UPDATE_WEEKLY_BY_DAY: {
      newState = { ...state, byDay: action.payload };
      break;
    }
    case UPDATE_MONTHLY_BY_WEEKDAY: {
      newState = { ...state, byDay: action.payload, byMonthDay: undefined, bySetPos: undefined };
      break;
    }
    case UPDATE_MONTHLY_BY_DAYS_IN_MONTH: {
      newState = { ...state, byMonthDay: action.payload, byDay: undefined, bySetPos: undefined };
      break;
    }
    case UPDATE_MONTHLY_BY_MONTHDAY: {
      newState = { ...state, byMonthDay: action.payload, byDay: undefined, bySetPos: undefined };
      break;
    }
    case UPDATE_MONTHLY_BY_DAY_OF_WEEK: {
      newState = {
        ...state,
        byMonthDay: undefined,
        byDay: action.payload.dayOfWeek,
        bySetPos: action.payload.occurences,
      };
      break;
    }
    case UPDATE_EVERY_X_INTERVAL: {
      newState = { ...state, interval: action.payload };
      break;
    }
    case UPDATE_YEARLY_MONTHS: {
      newState = { ...state, byMonth: action.payload.months };
      break;
    }
    case UPDATE_MULTIPLE_BY_DATES: {
      newState = { ...state, byDay: undefined, byDates: _uniq(action.payload) };
      break;
    }
    case UPDATE_DUE_ON: {
      newState = { ...state, dueOn: action.payload.startOf('day') };
      break;
    }
    case UPDATE_DEFAULT: {
      newState = {
        ...createValidDefaultRecurrenceForAlias({
          alias: action.payload.alias,
          dueOn: action.payload.dueOn,
          endOn: action.payload.endOn,
        }),
      };
      break;
    }
    case VALIDATE_RECURRENCE:
    default:
      newState = state;
  }

  return calculateSideEffects(newState);
};

// for a given alias and dueOn date, return a valid recurrence to use as the default
// called when UPDATE_DEFAULT is dispatched
const createValidDefaultRecurrenceForAlias = ({
  alias,
  dueOn,
  endOn,
}: { alias: string; dueOn: DateTime; endOn: DateTime | undefined }): RecurrenceType => {
  const byDay = indexToWeekday((dueOn || DateTime.local()).localWeekday);
  const byMonthDay = [(dueOn || DateTime.local()).day];
  const byMonth = [(dueOn || DateTime.local()).month];
  const todayByDate = `${byMonth}/${byMonthDay}`;

  let defaultRecurrence;

  switch (alias) {
    case oneTimeSimplifi:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.DAILY, interval: 1, count: 1 };
      break;
    case everyWeekSimplifi:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.WEEKLY, interval: 1, byDay };
      break;
    case everyMonthSimplifi:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.MONTHLY, interval: 1, byMonthDay };
      break;
    case everyYearSimplifi:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.YEARLY, interval: 1, byMonth, byMonthDay };
      break;
    case everyQuarterSimplifi:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.MONTHLY, interval: 3 };
      break;
    case everyXDays:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.DAILY, interval: 2 };
      break;
    case everyXWeeks:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.WEEKLY, interval: 2, byDay };
      break;
    case everyXMonths:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.MONTHLY, interval: 2, byMonthDay };
      break;
    case everyXYears:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.YEARLY, interval: 2, byMonth, byMonthDay };
      break;
    case multipleDatesSimplifi:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.YEARLY, interval: 1, byDates: [todayByDate] };
      break;
    case twiceAMonthSimplifi:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.MONTHLY, interval: 1, byMonthDay: [1, 15] };
      break;
    default:
      defaultRecurrence = { ...clearState, frequency: FrequencyEnum.MONTHLY, interval: 1, byMonthDay }; // MONTHLY
  }

  return { ...defaultRecurrence, alias, dueOn, endOn };
};

/////////////////////////////////////////////////
//   Recurrence utility functions
/////////////////////////////////////////////////
export const getNextDateFromRRule = (rrule: RRule, after: Date): DateTime | null => {
  const afterDate =
    after ||
    DateTime.local()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .setZone('utc', { keepLocalTime: true })
      .toJSDate();
  const jsDueOn = rrule.after(afterDate, true);
  return jsDueOn ? DateTime.fromJSDate(jsDueOn) : null;
};

export const isStateEqualProps = (state: RecurrenceType, recurrence: RecurrenceType): boolean => {
  const filterObject = (obj: RecurrenceType): RecurrenceType => {
    let filtered = { ...obj };
    delete filtered.isValid; // ignore isValid
    delete filtered.alias;
    delete filtered.dueOn; // we will compare this sperately
    filtered = _omitBy(filtered, _isNil);
    return filtered;
  };

  const filteredState = filterObject(state);
  const filteredRecurrence = filterObject(recurrence);
  const isEqual =
    _isEqual(filteredState, filteredRecurrence) &&
    filteredState.dueOn?.toISODate() === filteredRecurrence.dueOn?.toISODate();
  return isEqual;
};

// return an array of unique integers with the negative values as the tail in ascending order
export const sortValuesContainingLast = (values: number[]): number[] => {
  if (values?.length > 0) {
    const sorted = _uniq(values).sort((a: number, b: number) => a - b);
    return _concat(
      _takeRightWhile(sorted, (val: number) => val > 0),
      _takeWhile(sorted, (val: number) => val < 1),
    );
  }
  return [];
};

export const is90Days = (state: RecurrenceType): boolean =>
  state.frequency === FrequencyEnum.DAILY && state.interval === 90;
export const is3Months = (state: RecurrenceType): boolean =>
  state.frequency === FrequencyEnum.MONTHLY && state.interval === 3;

export const mapRecurrenceFrequencyToFrequencyAlias = (recurrence: RecurrenceType): string => {
  if (recurrence.alias) {
    return converQCSAliasToClientAlias(recurrence.alias);
  }

  // alias was not explicitly stored, so we have to infer it.
  let frequencyAlias = everyMonthSimplifi; // default to every month incase it misses somehow as a txn could be in a weird state after merge
  const { frequency, interval, count, byMonthDay, byDates } = recurrence || {};

  if (frequency && interval) {
    if (frequency === FrequencyEnum.DAILY && interval === 1 && count === 1) {
      frequencyAlias = oneTimeSimplifi;
    } else if (frequency === FrequencyEnum.DAILY && interval >= 1) {
      frequencyAlias = everyXDays;
    } else if (frequency === FrequencyEnum.MONTHLY && interval === 3) {
      frequencyAlias = everyQuarterSimplifi;
    } else if (frequency === FrequencyEnum.MONTHLY && interval === 1 && byMonthDay?.length === 2) {
      frequencyAlias = twiceAMonthSimplifi;
    } else if (frequency === FrequencyEnum.WEEKLY && interval === 1) {
      frequencyAlias = everyWeekSimplifi;
    } else if (frequency === FrequencyEnum.WEEKLY && interval > 1) {
      frequencyAlias = everyXWeeks;
    } else if (frequency === FrequencyEnum.MONTHLY && interval === 1) {
      frequencyAlias = everyMonthSimplifi;
    } else if (frequency === FrequencyEnum.MONTHLY && interval > 1) {
      frequencyAlias = everyXMonths;
    } else if (frequency === FrequencyEnum.YEARLY && interval === 1 && byDates && byDates?.length > 0) {
      frequencyAlias = multipleDatesSimplifi;
    } else if (frequency === FrequencyEnum.YEARLY && interval === 1) {
      frequencyAlias = everyYearSimplifi;
    } else if (frequency === FrequencyEnum.YEARLY && interval > 1) {
      frequencyAlias = everyXYears;
    }
  }
  return frequencyAlias;
};

export const recurrenceRowAlias = (recurrence: RecurrenceType): string => {
  let frequencyAlias = 'Unknown'; // default to Unknown incase it misses somehow as a txn could be in a weird state after merge
  const { frequency, interval, count, byMonthDay, byDates } = recurrence || {};

  if (frequency && interval) {
    if (frequency === FrequencyEnum.DAILY && interval === 1 && count === 1) {
      frequencyAlias = oneTimeSimplifi;
    } else if (frequency === FrequencyEnum.MONTHLY && interval === 3) {
      frequencyAlias = everyQuarterSimplifi;
    } else if (frequency === FrequencyEnum.MONTHLY && interval === 1 && byMonthDay?.length === 2) {
      frequencyAlias = twiceAMonthSimplifi;
    } else if (frequency === FrequencyEnum.WEEKLY && interval === 1) {
      frequencyAlias = everyWeekSimplifi;
    } else if (frequency === FrequencyEnum.MONTHLY && interval === 1) {
      frequencyAlias = everyMonthSimplifi;
    } else if (frequency === FrequencyEnum.MONTHLY && interval === 12) {
      frequencyAlias = everyYearSimplifi;
    } else if (frequency === FrequencyEnum.YEARLY && interval === 1 && byDates && byDates?.length > 0) {
      frequencyAlias = multipleDatesSimplifi;
    } else if (frequency === FrequencyEnum.YEARLY && interval === 1) {
      frequencyAlias = everyYearSimplifi;
    } else if (frequency === FrequencyEnum.DAILY) {
      frequencyAlias = interval === 1 ? 'Every day' : `Every ${interval} days`;
    } else if (frequency === FrequencyEnum.WEEKLY) {
      frequencyAlias = `Every ${interval} weeks`;
    } else if (frequency === FrequencyEnum.MONTHLY) {
      frequencyAlias = `Every ${interval} months`;
    } else if (frequency === FrequencyEnum.YEARLY) {
      frequencyAlias = `Every ${interval} years`;
    }
  }
  return frequencyAlias;
};

const rruleFromRecurrence = (recurrence: RecurrenceType, startDate: Date, endDate?: Date): RRule => {
  const {
    frequency,
    interval,
    count,
    byMonth: bymonth,
    bySetPos,
    byMonthDay: bymonthday,
    byYearDay: byyearday,
    byDay,
  } = recurrence;

  let byweekday;
  let bysetpos;

  // Because of an issue with RRule.toText(), we need to use an alternate method to construct the rrule
  // See: https://github.com/jkbrzt/rrule/issues/436
  // handle converting byDay + bySetPos to byweekday
  if (byDay && byDay.length > 0) {
    if (bySetPos && bySetPos.length > 0) {
      byweekday = bySetPos.map((index) => new Weekday(weekDays.indexOf(byDay[0]), index));
      bysetpos = null;
    } else {
      byweekday = byDay.map((day) => weekDays.indexOf(day));
    }
  }

  const freq = frequency && FREQUENCIES.indexOf(frequency);
  const validatedStartDate = startDate && isValidDate(startDate) ? startDate : undefined;
  const validatedEndtDate = endDate && isValidDate(endDate) ? endDate : undefined;
  const recur = {
    tzid: 'utc',
    dtstart: validatedStartDate,
    until: validatedEndtDate,
    wkst: 6,
    freq,
    interval,
    count,
    byweekday,
    bymonth,
    bysetpos,
    bymonthday,
    byyearday,
  };
  const newRRule = new RRule(recur);

  return newRRule;
};

const rruleAsText = (rrule: RRule): string => {
  let text = '';

  if (rrule.toString().indexOf('FREQ=YEARLY;INTERVAL=1;COUNT=1') > 0) {
    // handle the one-time case which is not really an rrule but we indiciat this case with string above
    text = `One payment on ${rrule.options.dtstart.toLocaleString()}.`;
  } else {
    text = `Repeats ${rrule.toText().replace(' (~ approximate)', '.')}`;
  }

  return text;
};

const formatList = (items: string[]): string => {
  if (items.length === 1) {
    return items[0];
  } else if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  } else {
    const lastItem = items.pop();
    return `${items.join(', ')}, and ${lastItem}`;
  }
};

const manualRecurrenceText = (recurrence: RecurrenceType): string => {
  let textDates: string[] = [];

  if (recurrence.byDates) {
    const adjustedDates = convertByDatesToAbsolute(recurrence.byDates);
    textDates = adjustedDates.map((date) => {
      const [month, day] = date?.split('/').map(Number) || [null, null];

      if (month && day) {
        if (day === -1) {
          return `last of ${SELECT_MONTHS[month - 1].label}`;
        } else {
          return `${ordinal(day)} of ${SELECT_MONTHS[month - 1].label}`;
        }
      } else {
        return '';
      }
    });
  }

  return formatList(textDates);
};

// convert an array of strings representing MONTH/DAY (i.e. ['6/23', '6/2', '1/2']) into a sorted array of valid JSDates
export const convertPartialDateToDateTime = (dates: string[]): Date[] => {
  return dates
    .map((partial) => {
      let [month, day] = partial.split('/');
      month = month.length === 1 ? '0' + month : month;
      day = day.length === 1 ? '0' + day : day;
      const year = DateTime.local().year;

      const dateString = `${year}-${month}-${day}`;
      const newDate = DateTime.fromISO(dateString);

      // if the date has passed, increment the year by 1
      return (newDate < DateTime.local() ? newDate.plus({ year: 1 }) : newDate).toJSDate();
    })
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
};

export const normalizeToUTC = (date: Date) => {
  const utcYear = date.getUTCFullYear();
  const utcMonth = date.getUTCMonth();
  const utcDate = date.getUTCDate();
  return new Date(utcYear, utcMonth, utcDate, 0, 0, 0, 0);
};

export const recurrenceInfo = ({
  recurrence,
  startDate,
  endDate,
}: RecurrenceWithDates): { text: string; nextDates: Date[] } => {
  const response: { text: string; nextDates: Date[] } = { text: '', nextDates: [] };

  const startJSDate = startDate?.startOf('day').toJSDate();
  let endJSDate = endDate?.startOf('day').toJSDate();

  // Math across DST boundries - if DST ends during interval, we need to normalize the times,
  // otherwise end interval will be excluded by Rrule since it will having occured outside of the range
  if (startDate && endDate && startDate.offset < endDate.offset) {
    endJSDate = endDate?.startOf('day').plus({ hours: 1 }).toJSDate();
  }

  if (isValidRecurrence(recurrence) && isValidDate(startJSDate)) {
    const alias = mapRecurrenceFrequencyToFrequencyAlias(recurrence);
    if (alias === multipleDatesSimplifi) {
      response.text = `Repeats every ${manualRecurrenceText(recurrence)}.`;
      const nextDates = recurrence.byDates && convertPartialDateToDateTime(recurrence.byDates);
      response.nextDates = nextDates || [];
    } else if (alias === oneTimeSimplifi) {
      response.text = `One time payment on ${startJSDate.toDateString()}.`;
      response.nextDates = [startJSDate];
    } else {
      const rrule = rruleFromRecurrence(recurrence, startJSDate, endJSDate);
      response.text = rrule && rruleAsText(rrule);
      response.nextDates = rrule.all((_date: Date, i: number) => i < 6).map((date: Date) => normalizeToUTC(date));
    }
  }

  return response;
};

////////////////////////////
// Form validation functions
/////////////////////////////

export const setErrorFields = (transactionValues: TransactionProps): object => {
  const { payee, amount, accountId } = transactionValues;
  const transactionVals = { payee: '', amount: '', accountId: '' };
  if (!payee || payee.trim().length === 0) {
    transactionVals.payee = 'Required';
  }
  if (amount === undefined) {
    transactionVals.amount = 'Required';
  }
  if (accountId === '') {
    transactionVals.accountId = 'Required';
  }
  return {
    transaction: transactionVals,
  };
};

export const verifyValues = (values: { transaction: any; virtualRecurrence: RecurrenceType }): boolean => {
  const { transaction, virtualRecurrence } = values;
  const { accountId, payee, amount } = transaction;
  const isValidBasic = payee.trim() !== '' && accountId !== '' && amount !== undefined && amount !== '';
  if (isValidBasic) {
    return isValidRecurrence(virtualRecurrence);
  }
  return false;
};
